import React from 'react';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { useEmailValidation } from '../hooks/useEmailValidation';

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  checkEndpoint: string;
  excludeId?: string;
  externalError?: string;
  dataTestId?: string;
  className?: string;
}

/**
 * Composant EmailInput avec validation en temps réel
 * Utilise useEmailValidation (conforme CONTEXT.md : pas de useEffect pour data fetching)
 * Style 100% Tailwind
 */
export const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChange,
  label = 'Email',
  placeholder = 'exemple@domain.com',
  required = false,
  checkEndpoint,
  excludeId,
  externalError,
  dataTestId,
  className = '',
}) => {
  const { error, isChecking, validateEmail } = useEmailValidation({
    endpoint: checkEndpoint,
    debounceMs: 500,
    excludeId,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toLowerCase();
    onChange(newValue);
    validateEmail(newValue);
  };

  // Erreur affichée : externe (du parent) ou interne (validation)
  const displayError = externalError || error;
  const hasError = !!displayError;
  const isValid = !hasError && !isChecking && value.trim().length > 0;

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-700">
          {label} {required && <span className="tw-text-red-500">*</span>}
        </label>
      )}

      <div className="tw-relative">
        {/* Icône Mail */}
        <Mail className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-5 tw-h-5 tw-text-gray-400" />

        {/* Input */}
        <input
          type="email"
          data-testid={dataTestId}
          value={value}
          onChange={handleChange}
          required={required}
          placeholder={placeholder}
          className={`tw-w-full tw-pl-10 tw-pr-10 tw-py-1.5 tw-text-sm tw-border tw-rounded-lg
            ${hasError ? 'tw-border-red-500' : 'tw-border-gray-300'}
            focus:tw-outline-none focus:tw-ring-2
            ${hasError ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary'}`}
        />

        {/* Indicateur de statut (droite) */}
        <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
          {isChecking && (
            <Loader2 className="tw-w-5 tw-h-5 tw-text-affinicar-primary tw-animate-spin" />
          )}
          {isValid && (
            <CheckCircle2 className="tw-w-5 tw-h-5 tw-text-green-600" />
          )}
        </div>
      </div>

      {/* Message d'erreur */}
      {displayError && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{displayError}</p>
      )}
    </div>
  );
};
